import React, { useEffect, useState, useCallback } from 'react';
import { Users, TrendingUp, CheckCircle, UserPlus, Activity, BarChart3, PieChart as PieIcon, UsersRound, AlertTriangle, Settings, RotateCcw } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
  BarChart, Bar,
} from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import { learnersService } from '../services/learners';
import { enrollmentsService } from '../services/enrollments';
import { organizationsService } from '../services/organizations';
import { getLearnerTrackingMap, LearnerTracking } from '../services/learnerTracking';
import { supabase } from '../config/supabase';
import { LearnerWithDetails, Organization } from '../types';
import { PeriodSelector, PeriodType, DateRange, getDateRangeForPeriod } from '../components/Dashboard/PeriodSelector';
import { AccountBadge, TrackingBadge } from '../components/LearnerBadges';

// ── Alert thresholds (defaults) ──
const DEFAULT_DELAY_DAYS    = 14;
const DEFAULT_INACTIVE_DAYS = 21;

interface AlertItem {
  learnerId: string;
  learnerName: string;
  courseName: string;
  progressPercent: number;
  alertType: 'delayed' | 'inactive';
  daysSince: number;
}

// ── Design tokens (Ondako Playful Home) ──
const CORAL = '#af3216';
const PEACH = '#ffac9a';
const MINT  = '#2f9e5a';
const GOLD  = '#f39200';
const MUTED = '#b7b1ab';

interface ClientStats {
  learnersCount: number;
  activeLearnersCount: number;
  disabledLearnersCount: number;
  completionRate: number;
  averageProgress: number;
  newLearnersCount: number;
  completedInPeriodCount: number;
}

interface MonthlyPoint  { month: string; inscriptions: number; completions: number }
interface StatusPoint   { name: string; value: number; color: string }
interface TopCoursePoint{ name: string; inscrits: number; fill: string }
interface GroupPerfPoint{ name: string; inscriptions: number; terminees: number }

export const DashboardClient: React.FC = () => {
  const { profile } = useAuth();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [learners, setLearners] = useState<LearnerWithDetails[]>([]);
  const [stats, setStats] = useState<ClientStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('30days');
  const [dateRange, setDateRange] = useState<DateRange>(() => getDateRangeForPeriod('30days'));
  const [periodLabel, setPeriodLabel] = useState<string>('30 derniers jours');

  const [monthlyData, setMonthlyData] = useState<MonthlyPoint[]>([]);
  const [statusData, setStatusData] = useState<StatusPoint[]>([]);
  const [topCourses, setTopCourses] = useState<TopCoursePoint[]>([]);
  const [groupPerf, setGroupPerf] = useState<GroupPerfPoint[]>([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [delayThreshold, setDelayThreshold] = useState(
    () => parseInt(localStorage.getItem('dlm_delay_threshold') || String(DEFAULT_DELAY_DAYS))
  );
  const [inactiveThreshold, setInactiveThreshold] = useState(
    () => parseInt(localStorage.getItem('dlm_inactive_threshold') || String(DEFAULT_INACTIVE_DAYS))
  );

  const handlePeriodChange = useCallback((period: PeriodType, range: DateRange) => {
    setSelectedPeriod(period);
    setDateRange(range);
    const labels: Record<PeriodType, string> = {
      '7days':  '7 derniers jours',
      '30days': '30 derniers jours',
      '1year':  '1 an',
      'custom': 'Période sélectionnée',
    };
    setPeriodLabel(labels[period]);
  }, []);

  useEffect(() => {
    if (profile?.organizationId) loadAlerts();
  }, [delayThreshold, inactiveThreshold]);

  useEffect(() => {
    if (profile?.organizationId) {
      loadDashboardData(dateRange);
      loadChartsData();
      loadAlerts();
    } else {
      setLoading(false);
    }
  }, [profile?.organizationId, dateRange]);

  const loadChartsData = async () => {
    if (!profile?.organizationId) return;
    try {
      setChartsLoading(true);
      const orgId = profile.organizationId;

      const [monthly, enrollmentStats, top, groupRates] = await Promise.all([
        enrollmentsService.getMonthlyStats(orgId, 7),
        enrollmentsService.getOrganizationStats(orgId),
        enrollmentsService.getTopCourses(orgId, 5),
        enrollmentsService.getGroupCompletionRates(orgId),
      ]);

      setMonthlyData(
        monthly.map((m: { monthKey: string; inscriptions: number; completions: number }) => ({
          month: m.monthKey,
          inscriptions: m.inscriptions,
          completions: m.completions,
        }))
      );

      const total = enrollmentStats.totalEnrollments;
      if (total > 0) {
        setStatusData([
          { name: 'Terminés',      value: enrollmentStats.completedEnrollments,  color: MINT  },
          { name: 'En cours',      value: enrollmentStats.inProgressEnrollments, color: GOLD  },
          { name: 'Non commencés', value: enrollmentStats.notStartedEnrollments, color: MUTED },
        ]);
      } else {
        setStatusData([]);
      }

      const barColors = [CORAL, PEACH, '#cf6847', '#df8462', '#efa07d'];
      setTopCourses(
        top.map((c: { courseName: string; inscrits: number }, i: number) => ({
          name: c.courseName.length > 28 ? c.courseName.slice(0, 28) + '...' : c.courseName,
          inscrits: c.inscrits,
          fill: barColors[i] || PEACH,
        }))
      );

      setGroupPerf(
        groupRates.slice(0, 8).map((g: { groupName: string; totalEnrollments: number; completedEnrollments: number }) => ({
          name: g.groupName.length > 22 ? g.groupName.slice(0, 22) + '...' : g.groupName,
          inscriptions: g.totalEnrollments,
          terminees: g.completedEnrollments,
        }))
      );
    } catch (error) {
      console.error('Error loading charts:', error);
    } finally {
      setChartsLoading(false);
    }
  };

  const loadAlerts = async () => {
    if (!profile?.organizationId) return;
    try {
      setAlertsLoading(true);
      const now = new Date();
      const date14DaysAgo = new Date(now.getTime() - delayThreshold   * 86400000).toISOString();
      const date21DaysAgo = new Date(now.getTime() - inactiveThreshold * 86400000).toISOString();
      const orgId = profile.organizationId;

      const [inactiveResult, delayedResult] = await Promise.all([
        supabase
          .from('enrollments')
          .select('id, enrolled_at, learner_id, course_id, learners!inner(fullname, organization_id, status), courses!inner(name), course_progress(progress_percent, completed_at)')
          .eq('learners.organization_id', orgId)
          .lt('enrolled_at', date21DaysAgo),
        supabase
          .from('enrollments')
          .select('id, enrolled_at, learner_id, course_id, learners!inner(fullname, organization_id, status), courses!inner(name), course_progress!inner(progress_percent, completed_at, last_activity_at)')
          .eq('learners.organization_id', orgId)
          .gt('course_progress.progress_percent', 0)
          .is('course_progress.completed_at', null)
          .lt('course_progress.last_activity_at', date14DaysAgo),
      ]);

      const alertItems: AlertItem[] = [];
      const nowMs = now.getTime();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const e of ((inactiveResult.data || []) as any[])) {
        const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
        if (cp && (cp.progress_percent > 0 || cp.completed_at)) continue;
        const learner = Array.isArray(e.learners) ? e.learners[0] : e.learners;
        if (learner?.status === 'inactive') continue; // compte desactive => pas de suivi
        const course  = Array.isArray(e.courses)  ? e.courses[0]  : e.courses;
        const daysSince = Math.floor((nowMs - new Date(e.enrolled_at).getTime()) / 86400000);
        alertItems.push({
          learnerId:       e.learner_id,
          learnerName:     learner?.fullname || 'Inconnu',
          courseName:      course?.name || 'Inconnu',
          progressPercent: 0,
          alertType:       'inactive',
          daysSince,
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const e of ((delayedResult.data || []) as any[])) {
        const cp = Array.isArray(e.course_progress) ? e.course_progress[0] : e.course_progress;
        const learner = Array.isArray(e.learners) ? e.learners[0] : e.learners;
        if (learner?.status === 'inactive') continue; // compte desactive => pas de suivi
        const course  = Array.isArray(e.courses)  ? e.courses[0]  : e.courses;
        const lastActivity = cp?.last_activity_at;
        const daysSince = lastActivity ? Math.floor((nowMs - new Date(lastActivity).getTime()) / 86400000) : 0;
        alertItems.push({
          learnerId:       e.learner_id,
          learnerName:     learner?.fullname || 'Inconnu',
          courseName:      course?.name || 'Inconnu',
          progressPercent: cp?.progress_percent || 0,
          alertType:       'delayed',
          daysSince,
        });
      }

      alertItems.sort((a, b) => b.daysSince - a.daysSince);
      setAlerts(alertItems);
    } catch (error) {
      console.error('Error loading alerts:', error);
    } finally {
      setAlertsLoading(false);
    }
  };

  const loadDashboardData = async (range: DateRange) => {
    if (!profile?.organizationId) return;
    try {
      setLoading(true);

      const [org, learnersData, enrollmentStats] = await Promise.all([
        organizationsService.getById(profile.organizationId),
        learnersService.getByOrganization(profile.organizationId),
        enrollmentsService.getOrganizationStats(profile.organizationId),
      ]);

      setOrganization(org);
      setLearners(learnersData);

      const totalLearners    = learnersData.length;
      const disabledLearners = learnersData.filter(l => l.status === 'inactive').length;
      // « Actif » = tout ce qui n'est pas desactive (valeur inconnue => Actif par defaut)
      const activeLearners   = learnersData.filter(l => l.status !== 'inactive').length;
      const avgProgress      = totalLearners > 0
        ? Math.round(learnersData.reduce((sum, l) => sum + l.overallProgress, 0) / totalLearners)
        : 0;

      const startTime = range.startDate.getTime();
      const endTime   = range.endDate.getTime();

      const newLearnersInPeriod = learnersData.filter(l => {
        if (!l.createdAt) return false;
        const t = new Date(l.createdAt).getTime();
        return t >= startTime && t <= endTime;
      }).length;

      setStats({
        learnersCount:          totalLearners,
        activeLearnersCount:    activeLearners,
        disabledLearnersCount:  disabledLearners,
        completionRate:         enrollmentStats.totalEnrollments > 0
          ? Math.round((enrollmentStats.completedEnrollments / enrollmentStats.totalEnrollments) * 100)
          : 0,
        averageProgress:        avgProgress,
        newLearnersCount:       newLearnersInPeriod,
        completedInPeriodCount: enrollmentStats.completedEnrollments,
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  // Suivi « vue d'ensemble » pour la petite table « Mes apprenants » : MEME logique
  // que la page « Mes apprenants » (agrege toutes les formations de l'apprenant).
  // Independant du widget « Apprenants necessitant une attention » (qui reste par formation).
  const [overviewMap, setOverviewMap] = useState<Map<string, LearnerTracking>>(new Map());
  useEffect(() => {
    if (learners.length === 0) { setOverviewMap(new Map()); return; }
    getLearnerTrackingMap(learners.map(l => l.id)).then(setOverviewMap);
  }, [learners, delayThreshold, inactiveThreshold]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral" />
      </div>
    );
  }

  if (!profile?.organizationId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Users className="w-16 h-16 mx-auto mb-4 text-cream-high" />
        <h2 className="text-xl font-semibold text-ink mb-2">
          Aucune organisation associee
        </h2>
        <p className="text-ink-soft">
          Veuillez contacter l'administrateur pour etre associe a une organisation.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink">Tableau de bord</h1>
          <p className="text-ink-soft mt-1">{organization?.name || 'Mon organisation'}</p>
        </div>
        <PeriodSelector selectedPeriod={selectedPeriod} onPeriodChange={handlePeriodChange} currentRange={dateRange} />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Users}       label="Total apprenants"     value={stats?.learnersCount || 0}
          bgColor="#e7deff" iconColor="#634ac1"
          subtitle={`${stats?.activeLearnersCount || 0} actifs · ${stats?.disabledLearnersCount || 0} désactivés`} />
        <KPICard icon={UserPlus}    label="Nouveaux apprenants"  value={stats?.newLearnersCount || 0}
          bgColor="#ffac9a" iconColor="#af3216" subtitle={periodLabel} />
        <KPICard icon={CheckCircle} label="Formations terminées" value={stats?.completedInPeriodCount || 0}
          bgColor="#c8efd4" iconColor="#2f9e5a" subtitle={periodLabel} />
        <KPICard icon={TrendingUp}  label="Taux de complétion"   value={`${stats?.completionRate || 0}%`}
          bgColor="#fdd34d" iconColor="#745c00" subtitle="global" />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Evolution — AreaChart */}
        <div className="lg:col-span-2 bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <Activity size={20} style={{ color: CORAL }} />
            <h2 className="text-lg font-semibold text-ink">Évolution des inscriptions</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : monthlyData.length === 0 ? (
            <ChartEmpty label="Aucune donnée pour cette période" />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={monthlyData}>
                <defs>
                  <linearGradient id="gradCoralClient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={CORAL} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={CORAL} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gradPeachClient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"  stopColor={PEACH} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={PEACH} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="inscriptions" name="Inscriptions" stroke={CORAL} fill="url(#gradCoralClient)"
                  strokeWidth={2} dot={{ r: 4, fill: CORAL, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
                <Area type="monotone" dataKey="completions"  name="Complétions"  stroke={PEACH} fill="url(#gradPeachClient)"
                  strokeWidth={2} dot={{ r: 4, fill: PEACH, strokeWidth: 2, stroke: '#fff' }} activeDot={{ r: 6 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
          {!chartsLoading && monthlyData.length > 0 && (
            <div className="flex items-center justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CORAL }} />
                <span className="text-xs text-ink-soft">Inscriptions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PEACH }} />
                <span className="text-xs text-ink-soft">Complétions</span>
              </div>
            </div>
          )}
        </div>

        {/* Repartition — Donut */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <PieIcon size={20} style={{ color: MINT }} />
            <h2 className="text-lg font-semibold text-ink">Répartition</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : statusData.length === 0 ? (
            <ChartEmpty label="Aucune inscription" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={statusData} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={85} paddingAngle={3}
                    dataKey="value" strokeWidth={0}
                  >
                    {statusData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-col gap-2 mt-2">
                {statusData.map((s) => (
                  <div key={s.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                      <span className="text-xs text-ink-soft">{s.name}</span>
                    </div>
                    <span className="text-xs font-medium text-ink">{s.value}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Courses — BarChart */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <BarChart3 size={20} style={{ color: CORAL }} />
            <h2 className="text-lg font-semibold text-ink">Top formations</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : topCourses.length === 0 ? (
            <ChartEmpty label="Aucune formation inscrite" />
          ) : (
            <ResponsiveContainer width="100%" height={topCourses.length * 52 + 20}>
              <BarChart data={topCourses} layout="vertical" margin={{ left: 10, right: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                  tick={{ fontSize: 12, fill: MUTED }} width={160} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="inscrits" name="Inscrits" radius={[0, 6, 6, 0]} barSize={28}>
                  {topCourses.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Group Performance — Stacked BarChart */}
        <div className="bg-white shadow-ambient rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-6">
            <UsersRound size={20} style={{ color: MINT }} />
            <h2 className="text-lg font-semibold text-ink">Performances des groupes</h2>
          </div>
          {chartsLoading ? (
            <ChartSkeleton />
          ) : groupPerf.length === 0 ? (
            <ChartEmpty label="Aucun groupe avec des inscriptions" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height={groupPerf.length * 52 + 20}>
                <BarChart data={groupPerf} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee7e2" horizontal={false} />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: MUTED }} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" axisLine={false} tickLine={false}
                    tick={{ fontSize: 12, fill: MUTED }} width={140} />
                  <Tooltip content={<ChartTooltip />} />
                  <Bar dataKey="inscriptions" name="Inscriptions" stackId="a" fill={CORAL} radius={[0, 0, 0, 0]} barSize={28} />
                  <Bar dataKey="terminees"    name="Terminées"    stackId="a" fill={PEACH} radius={[0, 6, 6, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
              <div className="flex items-center justify-center gap-6 mt-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: CORAL }} />
                  <span className="text-xs text-ink-soft">Inscriptions</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PEACH }} />
                  <span className="text-xs text-ink-soft">Terminées</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Alerts Widget */}
      {!alertsLoading && (
        <div className="bg-white shadow-ambient rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-cream-high flex justify-between items-center">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className={alerts.length > 0 ? 'text-coral' : 'text-mint'} />
              <h2 className="text-lg font-semibold text-ink">Apprenants nécessitant une attention</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                alerts.length > 0 ? 'bg-coral text-white' : 'bg-mint-container text-mint'
              }`}>
                {alerts.length > 0 ? `${alerts.length} alerte${alerts.length > 1 ? 's' : ''}` : '0 alerte'}
              </span>
              <button
                onClick={() => setShowAlertSettings(v => !v)}
                className={`p-1.5 rounded-lg transition-colors ${
                  showAlertSettings ? 'bg-primary-container text-coral' : 'text-ink-soft hover:text-ink hover:bg-cream-high'
                }`}
                title="Regler les seuils"
              >
                <Settings size={16} />
              </button>
            </div>
          </div>

          {/* Threshold settings panel */}
          {showAlertSettings && (
            <div className="px-6 py-3 border-b border-cream-high bg-cream-high/30 flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-soft whitespace-nowrap">Seuil En retard</label>
                <input
                  type="number" min={1} max={90}
                  value={delayThreshold}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(90, parseInt(e.target.value) || DEFAULT_DELAY_DAYS));
                    setDelayThreshold(v);
                    localStorage.setItem('dlm_delay_threshold', String(v));
                  }}
                  className="w-16 px-2 py-1 text-sm border border-cream-high rounded-lg text-center bg-white text-ink focus:outline-none focus:ring-2 focus:ring-coral/30"
                />
                <span className="text-xs text-ink-soft">jours</span>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-ink-soft whitespace-nowrap">Seuil Inactif</label>
                <input
                  type="number" min={1} max={90}
                  value={inactiveThreshold}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(90, parseInt(e.target.value) || DEFAULT_INACTIVE_DAYS));
                    setInactiveThreshold(v);
                    localStorage.setItem('dlm_inactive_threshold', String(v));
                  }}
                  className="w-16 px-2 py-1 text-sm border border-cream-high rounded-lg text-center bg-white text-ink focus:outline-none focus:ring-2 focus:ring-coral/30"
                />
                <span className="text-xs text-ink-soft">jours</span>
              </div>
              <button
                onClick={() => {
                  setDelayThreshold(DEFAULT_DELAY_DAYS);
                  setInactiveThreshold(DEFAULT_INACTIVE_DAYS);
                  localStorage.setItem('dlm_delay_threshold', String(DEFAULT_DELAY_DAYS));
                  localStorage.setItem('dlm_inactive_threshold', String(DEFAULT_INACTIVE_DAYS));
                }}
                className="flex items-center gap-1 text-xs text-ink-soft hover:text-coral transition-colors"
                title="Réinitialiser les seuils"
              >
                <RotateCcw size={12} />
                Réinitialiser
              </button>
            </div>
          )}

          {alerts.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <p className="text-base text-ink font-medium">
                {'🎉'} Tout est a jour !
              </p>
              <p className="text-sm text-ink-soft mt-1">
                Aucun apprenant ne necessite d'attention particuliere aujourd'hui.
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-cream-high/30">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Apprenant</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Formation</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Progression</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Suivi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cream-high">
                    {(showAllAlerts ? alerts : alerts.slice(0, 10)).map((alert, i) => (
                      <tr key={`${alert.learnerId}-${i}`} className="hover:bg-cream-high/30">
                        <td className="px-6 py-3 text-sm font-medium text-coral whitespace-nowrap">{alert.learnerName}</td>
                        <td className="px-6 py-3 text-sm text-ink-soft">{alert.courseName}</td>
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[80px] overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{
                                  width: `${alert.progressPercent}%`,
                                  backgroundColor: alert.progressPercent >= 75 ? MINT : alert.progressPercent >= 50 ? GOLD : '#EF4444',
                                }}
                              />
                            </div>
                            <span className="text-xs text-ink-soft w-8 text-right">{alert.progressPercent}%</span>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <TrackingBadge alert={alert.alertType} daysSince={alert.daysSince} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {alerts.length > 10 && (
                <div className="px-6 py-3 border-t border-cream-high text-center">
                  <button
                    onClick={() => setShowAllAlerts(v => !v)}
                    className="text-sm text-coral hover:text-coral-dim font-medium"
                  >
                    {showAllAlerts ? 'Voir moins' : `Voir les ${alerts.length} alertes`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Learners Table */}
      <div className="bg-white shadow-ambient rounded-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-cream-high flex justify-between items-center">
          <h2 className="text-lg font-semibold text-ink">Mes apprenants</h2>
          <span className="text-sm text-ink-soft">
            {learners.length} apprenant{learners.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-cream-high/30">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Apprenant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Groupe</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Compte</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Suivi</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-soft uppercase tracking-[0.05em]">Progression</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-high">
              {learners.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-ink-soft">
                    Aucun apprenant pour le moment
                  </td>
                </tr>
              ) : (
                learners.slice(0, 10).map((learner) => (
                  <tr key={learner.id} className="hover:bg-cream-high/30">
                    <td className="px-6 py-4">
                      <div className="font-medium text-coral">{learner.fullname}</div>
                      <div className="text-sm text-ink-soft">{learner.email}</div>
                    </td>
                    <td className="px-6 py-4 text-ink-soft">
                      {learner.groups && learner.groups.length > 0
                        ? learner.groups.map(g => g.name).join(', ')
                        : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <AccountBadge status={learner.status} />
                    </td>
                    <td className="px-6 py-4">
                      <TrackingBadge
                        alert={overviewMap.get(learner.id)?.alert?.type}
                        daysSince={overviewMap.get(learner.id)?.alert?.daysSince}
                        accountStatus={learner.status}
                        compact
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-cream-high rounded-full max-w-[100px] overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-700"
                            style={{
                              width: `${learner.overallProgress}%`,
                              backgroundColor: learner.overallProgress >= 75 ? MINT : learner.overallProgress >= 50 ? GOLD : '#EF4444',
                            }}
                          />
                        </div>
                        <span className="text-sm text-ink-soft w-10">{learner.overallProgress}%</span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {learners.length > 10 && (
          <div className="px-6 py-3 border-t border-cream-high text-center">
            <span className="text-sm text-ink-soft">Et {learners.length - 10} autres apprenants...</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Shared sub-components ──

interface KPICardProps {
  icon: React.FC<{ className?: string; size?: string | number; style?: React.CSSProperties }>;
  label: string;
  value: string | number;
  bgColor: string;
  iconColor: string;
  subtitle?: string;
}

const KPICard: React.FC<KPICardProps> = ({ icon: Icon, label, value, bgColor, iconColor, subtitle }) => (
  <div className="bg-white shadow-ambient rounded-2xl p-5 hover:shadow-ambient-lg transition-shadow duration-200">
    <div className="flex items-center gap-3.5">
      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bgColor }}>
        <Icon size={22} style={{ color: iconColor }} />
      </div>
      <div className="min-w-0">
        <p className="text-2xl font-bold text-ink leading-none">{value}</p>
        <p className="text-xs text-ink-soft mt-1 truncate">{label}</p>
        {subtitle && <p className="text-xs text-ink-soft/70 truncate">{subtitle}</p>}
      </div>
    </div>
  </div>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-ambient rounded-xl px-4 py-3">
      {label && <p className="text-xs font-medium text-ink mb-1">{label}</p>}
      {payload.map((p: { name: string; value: number; color: string }, i: number) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
          <span className="text-xs text-ink-soft">{p.name} :</span>
          <span className="text-xs font-medium text-ink">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartSkeleton: React.FC = () => (
  <div className="flex items-center justify-center bg-cream-high/30 rounded-xl animate-pulse" style={{ height: 220 }}>
    <div
      className="animate-spin rounded-full h-8 w-8 border-2 border-transparent"
      style={{ borderTopColor: CORAL, borderRightColor: PEACH }}
    />
  </div>
);

const ChartEmpty: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex items-center justify-center text-sm text-ink-soft" style={{ height: 220 }}>
    {label}
  </div>
);
